import { Subtract } from "utility-types";
import * as React from "react";
import { Formik, FormikContext, connect } from "formik";
import hoistNonReactStatics from "hoist-non-react-statics";
import { getUnhandledProps } from "semantic-ui-react/dist/es/lib/index.js";

import {
  Form as SemanticForm,
  Checkbox as SemanticCheckbox,
} from "./semantic-ui";

interface ConnectedProps<Values = { [key: string]: any }> {
  formik: FormikContext<Values>;
}

type CheckboxProps = {
  name: string;
  value: string;
  onChange?: (event: React.SyntheticEvent) => void;
};

// A Semantic UI Checkbox element that tracks values and dispatches onChange
// events in a way that formik understands.
const Checkbox = connect(
  class Checkbox extends React.Component<CheckboxProps & ConnectedProps> {
    ref!: typeof SemanticCheckbox | null;

    render() {
      const { formik, ...rest } = this.props;
      return (
        <SemanticCheckbox
          {...rest}
          checked={formik.values[rest.name] === rest.value}
          onChange={this.handleChange}
          ref={(r) => (this.ref = r as any)}
        />
      );
    }

    handleChange = (e: React.SyntheticEvent) => {
      // The Semantic Checkbox component fires onChange events on arbitrary
      // elements in the field, but we need to have the events come from the
      // input itself.
      if (!this.ref) return;
      const input = (this.ref as any).inputRef;
      e.target = input;
      if (this.props.onChange) this.props.onChange(e);
    };
  }
);

const Radio = (props: any) => <Checkbox {...props} radio type="radio" />;
// Semantic UI's Form.Field has behavior hard-coded for Semantic UI Checkbox and
// Radio components. We need to reproduce that behavior using our custom
// Checkbox and Radio components.
const CheckboxField = ({ label, type, ...rest }: any) => {
  return (
    <SemanticForm.Field
      {...(SemanticForm.Field as any).handledProps.reduce(
        (acc: { [key: string]: any }, key: string) => {
          acc[key] = rest[key];
          return acc;
        },
        {}
      )}
      content={React.createElement(Checkbox, {
        label,
        type,
        ...getUnhandledProps(SemanticForm.Field, rest),
      } as any)}
    />
  );
};
const RadioField = (props: any) => (
  <CheckboxField {...props} type="radio" radio />
);

type ProvidedProps = {
  name: string;
  value: any;
  onBlur: (...args: any[]) => void;
  onChange: (...args: any[]) => void;
  error: any;
};

// A HOC that takes a Semantic UI field component and makes it aware of the
// current Formik state.
const withFormik = <V extends { [key: string]: any }, P extends ProvidedProps>(
  Component: React.ComponentType<P>
): React.ComponentType<{ name: string } & Subtract<P, ProvidedProps>> => {
  const WrapperComponent = connect(
    ({
      formik: { errors, touched, values, handleBlur, handleChange },
      name,
      ...rest
    }: {
      formik: FormikContext<V>;
      name: string;
    }) =>
      React.createElement(Component, {
        name,
        value: name && values[name],
        onBlur: name && handleBlur,
        onChange: name && handleChange,
        error: name && touched[name] && !!errors[name],
        ...rest,
      } as any)
  );
  return WrapperComponent as any;
};

type FormProps = {};

type IForm = React.ComponentType<FormProps> & {
  Group: React.ComponentType<any>;
  Input: React.ComponentType<any>;
  Checkbox: React.ComponentType<any>;
  Radio: React.ComponentType<any>;
  Field: React.ComponentType<any>;
};

// A Semantic UI Form element that is aware of the current Formik state.
const Form: IForm = connect(function FormikForm({
  formik: { handleReset, handleSubmit, isSubmitting, isValid, submitCount },
  ...rest
}: FormProps & ConnectedProps) {
  return (
    <SemanticForm
      onReset={handleReset}
      onSubmit={handleSubmit as any}
      loading={isSubmitting}
      error={submitCount > 0 && !isValid}
      {...rest}
    />
  );
}) as any;

// Copy all SemanticForm components into Form, then overwrite the ones we
// modified.
hoistNonReactStatics(Form, SemanticForm);
Form.Input = withFormik(SemanticForm.Input as any) as any;
Form.Checkbox = withFormik(CheckboxField) as any;
Form.Radio = withFormik(RadioField) as any;
Form.Field = withFormik(SemanticForm.Field) as any;

export { Checkbox, Formik, Form, Radio };
